/**
 * Validator factories. Each returns a pure function that maps a value to an
 * error message or null. Compose them with `validateField`.
 */
export type ValidatorFn = (value: unknown) => string | null;

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

export function required(message = "This field is required"): ValidatorFn {
  return (value) => (isBlank(value) ? message : null);
}

export function isoDate(message = "Enter a valid date"): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? null : message;
  };
}

export function integer(message = "Enter a whole number"): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return Number.isInteger(Number(value)) ? null : message;
  };
}

export function numberBetween(min: number, max: number, message?: string): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return message ?? "Enter a number";
    return parsed >= min && parsed <= max ? null : (message ?? `Enter a value between ${min} and ${max}`);
  };
}

export function maxLength(limit: number, message?: string): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return String(value).length <= limit ? null : (message ?? `Keep this under ${limit} characters`);
  };
}

export function ssn(message = "Enter a 9-digit SSN"): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return /^\d{9}$/.test(String(value).replace(/\D/g, "")) ? null : message;
  };
}

export function postalCode(message = "Enter a 5-digit ZIP or ZIP+4"): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return /^\d{5}(-?\d{4})?$/.test(String(value).trim()) ? null : message;
  };
}

export function oneOf(options: readonly string[], message = "Choose one of the listed values"): ValidatorFn {
  return (value) => {
    if (isBlank(value)) return null;
    return options.includes(String(value)) ? null : message;
  };
}

/** Runs validators in order and returns the first failure. */
export function validateField(value: unknown, validators: readonly ValidatorFn[]): string | null {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
}

export type FieldErrors<T extends string> = Partial<Record<T, string>>;

export function validateFields<T extends string>(values: Record<T, unknown>, rules: Record<T, readonly ValidatorFn[]>): FieldErrors<T> {
  const errors: FieldErrors<T> = {};
  for (const key of Object.keys(rules) as T[]) {
    const error = validateField(values[key], rules[key]);
    if (error) errors[key] = error;
  }
  return errors;
}

export function hasErrors<T extends string>(errors: FieldErrors<T>): boolean {
  return Object.keys(errors).length > 0;
}
